import React, { useState } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import DynamicTable from "../../../data/DynamicTable/dynamicTable";
import { allStatements } from "../../../data/Queries/SetupManagement/FinSightsQueries";
import { allStatementsColumns } from "../../../data/Columns/FSColumns";
import { reportManagementUrl } from "../../../config";
import StatementNarativeForm from "./form";

const base_url = reportManagementUrl.uri;
const API_BASE = "";
const BaseUrl = "https://fsbe.mcb.co.ke/finsightsgateway"

// ─── HELPERS ────────────────────────────────────────────────────────────────
const getToken = () => {
  let token = localStorage.getItem("token") || "";
  if (token.startsWith('"') && token.endsWith('"')) {
    token = token.slice(1, -1);
  }
  return token;
};

const scoreColor = (score) => {
  if (score >= 700) return "#3B6D11";
  if (score >= 550) return "#BA7517";
  if (score >= 400) return "#D85A30";
  return "#A32D2D";
};

const riskColor = (level) => {
  if (!level) return "#888780";
  const l = level.toLowerCase();
  if (l === "low") return "#3B6D11";
  if (l === "medium") return "#BA7517";
  return "#A32D2D";
};

const riskBg = (level) => {
  if (!level) return "#F1EFE8";
  const l = level.toLowerCase();
  if (l === "low") return "#EAF3DE";
  if (l === "medium") return "#FAEEDA";
  return "#FCEBEB";
};

// ─── STATUS BADGE ────────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const s = status?.toLowerCase() || "";
  const [bg, fg] = s === "processed" ? ["#EAF3DE", "#27500A"] : s === "processing" ? ["#FAEEDA", "#633806"] : s === "failed" ? ["#FCEBEB", "#791F1F"] : ["#F1EFE8", "#444441"];
  return <span style={{ background: bg, color: fg, fontSize: 11, fontWeight: 500, padding: "2px 10px", borderRadius: 99, textTransform: "capitalize" }}>{status || "Pending"}</span>;
};

// ─── CUSTOM CELL RENDERERS ───────────────────────────────────────────────────
const CustomScoreCell = ({ value }) => {
  if (!value && value !== 0) return <span style={{ color: "var(--color-text-secondary)", fontSize: 13 }}>—</span>;
  return <span style={{ fontSize: 14, fontWeight: 600, color: scoreColor(value) }}>{value}</span>;
};

const CustomRiskCell = ({ value }) => {
  if (!value) return <span style={{ color: "var(--color-text-secondary)", fontSize: 13 }}>—</span>;
  return <span style={{ background: riskBg(value), color: riskColor(value), fontSize: 11, fontWeight: 500, padding: "2px 10px", borderRadius: 99, textTransform: "capitalize" }}>{value}</span>;
};

const CustomStatusCell = ({ value }) => <StatusBadge status={value} />;

const CustomCustomerCell = ({ row }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
    <div style={{ width: 32, height: 32, borderRadius: 8, background: "#E6F1FB", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 600, color: "#185FA5", flexShrink: 0 }}>
      {(row.customer_name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("")}
    </div>
    <div>
      <p style={{ margin: 0, fontSize: 14, fontWeight: 500, color: "var(--color-text-primary)" }}>{row.customer_name || "—"}</p>
      {row.phone && <p style={{ margin: 0, fontSize: 12, color: "var(--color-text-secondary)" }}>{row.phone}</p>}
    </div>
  </div>
);

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
const StatementNarative = () => {
  const navigate = useNavigate();
  const [generatingId, setGeneratingId] = useState(null);
  const [toast, setToast] = useState(null);

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  // ── GENERATE NARRATIVE: POST -> navigate to generated page with result ──
  const handleGenerateNarrative = async (row) => {
    const id = row.id || row.statementid || row.statement_id;
    if (!id) {
      showToast("No statement ID found", "error");
      return;
    }

    setGeneratingId(id);

    try {
      const token = getToken();
      const res = await fetch(`${BaseUrl}/api/narrative/${id}`, {
        method: "GET",
        headers: {
          ...(token ? { "Authorization": `Bearer ${token}` } : {}),
        },
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}: ${await res.text()}`);

      const result = await res.json();
      console.log("Generate narrative response:", result);

      // Navigate to the generated narrative page with the result
      navigate("/generated-narrative", {
        state: {
          statementData: row,
          generatedResult: result,
        },
      });

    } catch (e) {
      showToast(e.message, "error");
    } finally {
      setGeneratingId(null);
    }
  };

  // ── VIEW NARRATIVE: navigate to structured report page ──
  const handleViewNarrative = (row) => {
    const id = row.id || row.statementid || row.statement_id;
    if (!id) {
      showToast("No statement ID found", "error");
      return;
    }
    localStorage.setItem("statementData", JSON.stringify(row));
    navigate("/view-narrative");
  };

  // Enhance columns with custom renderers
  const enhancedColumns = allStatementsColumns.map((col) => {
    if (col.field === "score" || col.accessorKey === "score") {
      return { ...col, Cell: CustomScoreCell };
    }
    if (col.field === "risk" || col.accessorKey === "risk") {
      return { ...col, Cell: CustomRiskCell };
    }
    if (col.field === "status" || col.accessorKey === "status") {
      return { ...col, Cell: CustomStatusCell };
    }
    if (col.field === "customer_name" || col.accessorKey === "customer_name") {
      return { ...col, Cell: ({ row }) => <CustomCustomerCell row={row.original || row} /> };
    }
    return col;
  });

  const actions = {
    entity: "Upload Statement",
    edit: {
      key: "EDIT",
      button_name: "Edit",
      Show_Button: false,
      permission: "EditPermission",
    },
    add: {
      key: "ADD",
      button_name: "Upload Statement",
      Show_Button: true,
      permission: "CreatePermission",
    },
    activateDeactivate: {
      key: "ACTIVATEDEACTIVATE",
      button_name: "Deactivate",
      Show_Button: false,
      permission: "ActivatePermission",
    },
    generateNarrative: {
      key: "GENERATE_NARRATIVE",
      button_name: "Generate Narrative",
      Show_Button: true,
      permission: "hasNoPermission",
      roleGroupId: [1, 2, 3, 4, 5],
      onClick: handleGenerateNarrative,
    },
    // viewNarrative: {
    //   key: "VIEW_NARRATIVE",
    //   button_name: "View Narrative",
    //   Show_Button: true,
    //   permission: "hasNoPermission",
    //   roleGroupId: [1, 2, 3, 4, 5],
    //   onClick: handleViewNarrative,
    // },
    viewReport: {
      key: "MARK_TASK_DONE",
      button_name: "View Report",
      Show_Button: true,
      permission: "hasNoPermission",
      roleGroupId: [1, 2, 3, 4, 5],
    },
    isUploadingData: true,
    showStatus: true,
  };

  return (
    <Box m="20px">
      {/* ── LOADING OVERLAY ── */}
      {generatingId !== null && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.45)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            gap: 2,
          }}
        >
          <CircularProgress size={48} sx={{ color: "#fff" }} />
          <Typography sx={{ color: "#fff", fontSize: 16, fontWeight: 500 }}>
            Generating narrative for statement #{generatingId}…
          </Typography>
        </Box>
      )}

      {/* Toast */}
      {toast && (
        <Box
          sx={{
            position: "fixed",
            bottom: 24,
            right: 24,
            zIndex: 2000,
            background: toast.type === "success" ? "#EAF3DE" : "#FCEBEB",
            border: `0.5px solid ${toast.type === "success" ? "#97C459" : "#F09595"}`,
            borderRadius: 2.5,
            px: 2.5,
            py: 1.5,
            fontSize: 14,
            color: toast.type === "success" ? "#27500A" : "#791F1F",
            display: "flex",
            gap: 1.25,
            alignItems: "center",
            boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
          }}
        >
          <i className={`ti ti-${toast.type === "success" ? "circle-check" : "alert-circle"}`} style={{ fontSize: 16 }} />
          {toast.msg}
        </Box>
      )}

      <DynamicTable
        title="Upload Statement Management"
        subtitle="Upload, generate narratives, and view credit reports"
        columns={enhancedColumns}
        FormComponent={StatementNarativeForm}
        query={allStatements}
        base_url={base_url}
        actions={actions}
      />
    </Box>
  );
};

export default StatementNarative;